export interface VADOptions {
  mode?: number;
  voiceThreshold?: number;
  silenceThreshold?: number;
  voiceTrigger?: number;
  silenceTrigger?: number;
  threshold?: number;
  minEnergy?: number;
  energyHistorySize?: number;
  shortSpeechEnabled?: boolean;
}

export class VAD {
  // Static enums for external reference
  static Mode = {
    QUALITY: 0,
    LOW_BITRATE: 1,
    AGGRESSIVE: 2,
    VERY_AGGRESSIVE: 3,
    NORMAL: 2,
  };

  static Event = {
    ERROR: -1,
    SILENCE: 0,
    VOICE: 1,
    NOISE: 2,
  };

  private mode: number;
  private silence: number = 0;
  private voice: number = 0;
  private noise: number = 0;

  private voiceThreshold: number;
  private silenceThreshold: number;
  private voiceTrigger: number;
  private silenceTrigger: number;
  private threshold: number;

  private consecutiveVoice: number = 0;
  private consecutiveSilence: number = 0;
  private status: number = VAD.Event.SILENCE;

  private minEnergy: number;
  private energyHistory: number[] = [];
  private energyHistorySize: number;
  private shortSpeechEnabled: boolean;

  constructor(options: VADOptions = {}) {
    this.mode = options.mode ?? VAD.Mode.NORMAL;

    this.voiceThreshold = options.voiceThreshold ?? 0.6;
    this.silenceThreshold = options.silenceThreshold ?? 0.2;
    this.voiceTrigger = options.voiceTrigger ?? 0.4;
    this.silenceTrigger = options.silenceTrigger ?? 0.3;
    this.threshold = options.threshold ?? 0.9;

    this.minEnergy = options.minEnergy ?? 150;
    this.energyHistorySize = options.energyHistorySize ?? 5;
    this.shortSpeechEnabled = options.shortSpeechEnabled !== false;
    console.log({
      silence: this.silence,
      voice: this.voice,
      noise: this.noise,
      mode: this.mode,
      voiceThreshold: this.voiceThreshold,
      silenceThreshold: this.silenceThreshold,
      voiceTrigger: this.voiceTrigger,
      silenceTrigger: this.silenceTrigger,
      threshold: this.threshold,
      minEnergy: this.minEnergy,
      energyHistorySize: this.energyHistorySize,
      shortSpeechEnabled: this.shortSpeechEnabled,
    });
  }

  public processAudio(buffer: Float32Array): number {
    let sum = 0;
    let maxAbs = 0;
    let validSamples = 0;

    for (let i = 0; i < buffer.length; i++) {
      const abs = Math.abs(buffer[i]);
      sum += abs;
      maxAbs = Math.max(maxAbs, abs);
      if (abs > 200) validSamples++;
    }

    const avg = sum / buffer.length;
    const validRatio = validSamples / buffer.length;

    this.energyHistory.push(avg);
    if (this.energyHistory.length > this.energyHistorySize) {
      this.energyHistory.shift();
    }

    let energyChange = 0;
    if (this.energyHistory.length > 1) {
      const prevAvg = this.energyHistory[this.energyHistory.length - 2];
      energyChange = (avg - prevAvg) / (prevAvg > 0 ? prevAvg : 1);
    }

    let isShortSpeech = false;
    if (this.shortSpeechEnabled) {
      isShortSpeech =
        (avg > this.minEnergy && energyChange > 0.5) ||
        (maxAbs > 3000 && validRatio > 0.05);
    }

    if (
      (avg > 200 && maxAbs > 1500 && validRatio > this.voiceThreshold) ||
      isShortSpeech
    ) {
      this.consecutiveVoice++;
      this.consecutiveSilence = 0;
      this.voice++;

      if (this.status !== VAD.Event.VOICE && this.consecutiveVoice >= 2) {
        this.status = VAD.Event.VOICE;
      }

      return VAD.Event.VOICE;
    } else if (avg < 100 || validRatio < this.silenceThreshold) {
      this.consecutiveSilence++;
      this.consecutiveVoice = 0;
      this.silence++;

      if (this.status !== VAD.Event.SILENCE && this.consecutiveSilence >= 3) {
        this.status = VAD.Event.SILENCE;
      }

      return VAD.Event.SILENCE;
    } else {
      if (this.status === VAD.Event.VOICE) {
        this.consecutiveVoice = Math.max(0, this.consecutiveVoice - 0.5);
        this.consecutiveSilence += 0.5;

        if (this.consecutiveSilence > 5) {
          this.status = VAD.Event.SILENCE;
          return VAD.Event.SILENCE;
        }

        this.voice++;
        return VAD.Event.VOICE;
      } else {
        this.silence++;
        return VAD.Event.SILENCE;
      }
    }
  }

  public setOptions(options: VADOptions = {}): void {
    this.mode = options.mode ?? this.mode;
    if (options.voiceThreshold !== undefined)
      this.voiceThreshold = options.voiceThreshold;
    if (options.silenceThreshold !== undefined)
      this.silenceThreshold = options.silenceThreshold;
    if (options.voiceTrigger !== undefined)
      this.voiceTrigger = options.voiceTrigger;
    if (options.silenceTrigger !== undefined)
      this.silenceTrigger = options.silenceTrigger;
    if (options.minEnergy !== undefined) this.minEnergy = options.minEnergy;
    if (options.shortSpeechEnabled !== undefined)
      this.shortSpeechEnabled = options.shortSpeechEnabled;
    if (options.threshold !== undefined) this.threshold = options.threshold;
  }
}
